import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import {
  AlertTriangle, CheckCircle2, RefreshCw, Filter, Trash2,
  ChevronDown, ChevronUp, Clock, Monitor, Zap, Search
} from 'lucide-react';

/* ═══ STYLED ═══ */
const Page = styled.div`
  padding: 20px 24px; height: 100%; overflow-y: auto;
  background: var(--bg-primary); color: var(--text-primary);
`;
const Header = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: 16px; flex-wrap: wrap;
  h2 { font-size: 18px; font-weight: 700; margin: 0; display: flex; align-items: center; gap: 8px; }
`;
const StatCards = styled.div`
  display: flex; gap: 10px; margin-bottom: 16px; flex-wrap: wrap;
`;
const StatCard = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 10px;
  padding: 10px 16px; min-width: 100px; flex: 1;
  .label { font-size: 10px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; }
  .value { font-size: 22px; font-weight: 800; margin-top: 2px; color: ${p => p.$color || 'var(--text-primary)'}; }
  .sub { font-size: 10px; color: var(--text-muted); margin-top: 2px; }
`;
const FiltersBar = styled.div`
  display: flex; gap: 8px; margin-bottom: 12px; flex-wrap: wrap; align-items: center;
`;
const FilterSelect = styled.select`
  background: var(--surface-hover); border: 1px solid var(--card-border); border-radius: 6px;
  color: var(--text-primary); padding: 5px 8px; font-size: 11px; cursor: pointer;
  &:focus { outline: none; border-color: var(--primary); }
`;
const SearchInput = styled.input`
  background: var(--surface-hover); border: 1px solid var(--card-border); border-radius: 6px;
  color: var(--text-primary); padding: 5px 10px; font-size: 11px; flex: 1; min-width: 150px;
  &::placeholder { color: var(--text-muted); }
  &:focus { outline: none; border-color: var(--primary); }
`;
const Btn = styled.button`
  display: flex; align-items: center; gap: 4px; padding: 5px 10px; font-size: 11px;
  border: 1px solid var(--card-border); border-radius: 6px; cursor: pointer;
  background: ${p => p.$danger ? '#ef444422' : p.$success ? '#22c55e22' : 'var(--surface-hover)'};
  color: ${p => p.$danger ? '#ef4444' : p.$success ? '#22c55e' : 'var(--text-primary)'};
  &:hover { opacity: 0.8; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
const Table = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 10px; overflow: hidden;
`;
const TableHeader = styled.div`
  display: grid; grid-template-columns: 30px 70px 120px 100px 1fr 100px 90px 60px;
  gap: 4px; padding: 8px 12px; font-size: 10px; font-weight: 700; color: var(--text-muted);
  text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid var(--card-border);
  background: var(--surface-hover);
`;
const Row = styled.div`
  display: grid; grid-template-columns: 30px 70px 120px 100px 1fr 100px 90px 60px;
  gap: 4px; padding: 8px 12px; font-size: 11px; border-bottom: 1px solid var(--card-border);
  cursor: pointer; transition: background .1s;
  &:hover { background: var(--surface-hover); }
  ${p => p.$expanded && 'background: var(--surface-hover);'}
  ${p => p.$resolved && 'opacity: 0.5;'}
`;
const LevelBadge = styled.span`
  display: inline-flex; align-items: center; gap: 3px; padding: 2px 6px; border-radius: 4px;
  font-size: 10px; font-weight: 700;
  ${p => p.$level === 'critical' && 'background: #ef444422; color: #ef4444;'}
  ${p => p.$level === 'error' && 'background: #f59e0b22; color: #f59e0b;'}
  ${p => p.$level === 'warning' && 'background: #eab30822; color: #eab308;'}
  ${p => p.$level === 'info' && 'background: #3b82f622; color: #3b82f6;'}
`;
const CategoryBadge = styled.span`
  padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 600;
  background: var(--surface-hover); color: var(--text-muted);
`;
const ExpandedDetail = styled.div`
  grid-column: 1 / -1; padding: 12px; background: #0a0a0a; border-radius: 8px;
  margin: 4px 0 8px; font-size: 11px; display: flex; flex-direction: column; gap: 8px;
  .section-title { font-size: 10px; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 2px; }
  pre { background: #111; padding: 8px; border-radius: 6px; font-size: 10px; overflow-x: auto; color: #ef4444; margin: 0;
    white-space: pre-wrap; word-break: break-all; max-height: 200px; overflow-y: auto; }
  .context { background: #111; padding: 8px; border-radius: 6px; font-size: 10px; color: #a78bfa; }
`;
const Pagination = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 8px; padding: 12px;
  font-size: 11px; color: var(--text-muted);
`;

/* ═══ COMPONENT ═══ */
export default function ErrorLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const LIMIT = 50;

  // Filters
  const [filterLevel, setFilterLevel] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterResolved, setFilterResolved] = useState('false');
  const [searchText, setSearchText] = useState('');
  const [statHours] = useState(24);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', LIMIT);
      params.set('offset', page * LIMIT);
      if (filterLevel) params.set('level', filterLevel);
      if (filterCategory) params.set('category', filterCategory);
      if (filterResolved) params.set('resolved', filterResolved);
      if (searchText) params.set('search', searchText);

      const res = await axios.get(`/api/error-logs?${params}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Error fetching logs:', err);
    }
    setLoading(false);
  }, [page, filterLevel, filterCategory, filterResolved, searchText]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`/api/error-logs/stats?hours=${statHours}`);
      setStats(res.data);
    } catch (_) {}
  }, [statHours]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleResolve = async (id) => {
    await axios.post(`/api/error-logs/${id}/resolve`);
    fetchLogs();
    fetchStats();
  };

  const handleResolveBulk = async (type, value) => {
    await axios.post('/api/error-logs/resolve-bulk', { [type]: value });
    fetchLogs();
    fetchStats();
  };

  const handleClearOld = async () => {
    if (!window.confirm('Supprimer les erreurs résolues de plus de 30 jours ?')) return;
    await axios.delete('/api/error-logs/clear?days=30');
    fetchLogs();
    fetchStats();
  };

  const totalPages = Math.ceil(total / LIMIT);

  const formatDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) + ' ' +
           dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <Page>
      <Header>
        <h2><AlertTriangle size={20} color="#f59e0b" /> Error Logs</h2>
        <Btn onClick={() => { fetchLogs(); fetchStats(); }} disabled={loading}>
          <RefreshCw size={12} className={loading ? 'spin' : ''} /> Refresh
        </Btn>
        <Btn $danger onClick={handleClearOld}><Trash2 size={12} /> Purger</Btn>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>
          {total} erreur{total !== 1 ? 's' : ''}
        </div>
      </Header>

      {/* Stats */}
      {stats && (
        <StatCards>
          <StatCard $color="#ef4444">
            <div className="label">Non résolues</div>
            <div className="value">{stats.rates?.unresolved || 0}</div>
          </StatCard>
          <StatCard $color="#f59e0b">
            <div className="label">Dernière heure</div>
            <div className="value">{stats.rates?.last_hour || 0}</div>
          </StatCard>
          <StatCard $color="#3b82f6">
            <div className="label">6 dernières h</div>
            <div className="value">{stats.rates?.last_6h || 0}</div>
          </StatCard>
          {stats.by_category?.slice(0, 4).map((c, i) => (
            <StatCard key={i}>
              <div className="label">{c.category}</div>
              <div className="value">{c.count}</div>
              <div className="sub">
                <Btn style={{ padding: '1px 4px', fontSize: 9 }} $success
                  onClick={() => handleResolveBulk('category', c.category)}>
                  <CheckCircle2 size={9} /> Tout résoudre
                </Btn>
              </div>
            </StatCard>
          ))}
        </StatCards>
      )}

      {/* Filters */}
      <FiltersBar>
        <Filter size={12} color="var(--text-muted)" />
        <FilterSelect value={filterLevel} onChange={e => { setFilterLevel(e.target.value); setPage(0); }}>
          <option value="">Tous niveaux</option>
          <option value="critical">Critical</option>
          <option value="error">Error</option>
          <option value="warning">Warning</option>
          <option value="info">Info</option>
        </FilterSelect>
        <FilterSelect value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setPage(0); }}>
          <option value="">Toutes catégories</option>
          <option value="adb">ADB</option>
          <option value="gemini">Gemini</option>
          <option value="instagram">Instagram</option>
          <option value="database">Database</option>
          <option value="network">Network</option>
          <option value="workflow">Workflow</option>
          <option value="script">Script</option>
          <option value="selector">Selector</option>
          <option value="server">Server</option>
          <option value="config">Config</option>
          <option value="unknown">Unknown</option>
        </FilterSelect>
        <FilterSelect value={filterResolved} onChange={e => { setFilterResolved(e.target.value); setPage(0); }}>
          <option value="">Tous</option>
          <option value="false">Non résolues</option>
          <option value="true">Résolues</option>
        </FilterSelect>
        <SearchInput placeholder="Rechercher dans les messages..."
          value={searchText} onChange={e => setSearchText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { setPage(0); fetchLogs(); } }} />
        <Btn onClick={() => { setPage(0); fetchLogs(); }}><Search size={12} /></Btn>
      </FiltersBar>

      {/* Table */}
      <Table>
        <TableHeader>
          <span></span>
          <span>Niveau</span>
          <span>Source</span>
          <span>Catégorie</span>
          <span>Message</span>
          <span>Device</span>
          <span>Date</span>
          <span>Actions</span>
        </TableHeader>
        {logs.map(log => (
          <React.Fragment key={log.id}>
            <Row $expanded={expandedId === log.id} $resolved={log.resolved}
              onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}>
              <span>{expandedId === log.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}</span>
              <span><LevelBadge $level={log.level}>{log.level}</LevelBadge></span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.source}>{log.source}</span>
              <span><CategoryBadge>{log.category}</CategoryBadge></span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.message}>{log.message}</span>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{log.device_id || '—'}</span>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{formatDate(log.created_at)}</span>
              <span>
                {!log.resolved && (
                  <Btn $success style={{ padding: '2px 5px' }}
                    onClick={(e) => { e.stopPropagation(); handleResolve(log.id); }}
                    title="Marquer comme résolu">
                    <CheckCircle2 size={10} />
                  </Btn>
                )}
              </span>
            </Row>
            {expandedId === log.id && (
              <div style={{ padding: '0 12px 8px', borderBottom: '1px solid var(--card-border)' }}>
                <ExpandedDetail>
                  <div>
                    <span className="section-title">Message complet</span>
                    <div style={{ color: 'var(--text-primary)', marginTop: 4 }}>{log.message}</div>
                  </div>
                  {log.stack_trace && (
                    <div>
                      <span className="section-title">Stack trace</span>
                      <pre>{log.stack_trace}</pre>
                    </div>
                  )}
                  {log.context && (
                    <div>
                      <span className="section-title">Contexte</span>
                      <div className="context">{JSON.stringify(log.context, null, 2)}</div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 12, fontSize: 10, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                    {log.account && <span>👤 {log.account}</span>}
                    {log.script_type && <span>📜 {log.script_type}</span>}
                    {log.workstation && <span><Monitor size={10} /> {log.workstation}</span>}
                    {log.session_id && <span><Zap size={10} /> {log.session_id}</span>}
                    <span><Clock size={10} /> {formatDate(log.created_at)}</span>
                    {log.resolved && <span style={{ color: '#22c55e' }}>✓ Résolu {formatDate(log.resolved_at)}</span>}
                  </div>
                </ExpandedDetail>
              </div>
            )}
          </React.Fragment>
        ))}
        {logs.length === 0 && !loading && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            <CheckCircle2 size={32} color="#22c55e" style={{ marginBottom: 8 }} /><br/>
            Aucune erreur trouvée
          </div>
        )}
      </Table>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination>
          <Btn disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Précédent</Btn>
          <span>Page {page + 1} / {totalPages}</span>
          <Btn disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Suivant →</Btn>
        </Pagination>
      )}
    </Page>
  );
}
